import type { SpecialtyKey } from '~/types/onboarding'
import type { LocalizedText } from '~/types/site'

export type SpecialtyServiceCopy = {
  title: LocalizedText
  description: LocalizedText
  features: LocalizedText[]
}

export type SpecialtyContent = {
  heroTitle: LocalizedText
  heroDescription: LocalizedText
  bannerTitle: LocalizedText
  doctorIntro: LocalizedText
  services: SpecialtyServiceCopy[]
  faq: Array<{ label: LocalizedText; content: LocalizedText }>
  testimonials: Array<{ name: LocalizedText; comment: LocalizedText }>
}

function t(fa: string, en: string): LocalizedText {
  return { fa, en }
}

function svc(
  titleFa: string,
  titleEn: string,
  descFa: string,
  descEn: string,
  features: Array<[string, string]>,
): SpecialtyServiceCopy {
  return {
    title: t(titleFa, titleEn),
    description: t(descFa, descEn),
    features: features.map(([fa, en]) => t(fa, en)),
  }
}

export const SPECIALTY_CONTENT: Record<SpecialtyKey, SpecialtyContent> = {
  cardiology: {
    heroTitle: t('مراقبت تخصصی قلب و عروق', 'Specialist cardiology care'),
    heroDescription: t(
      'ارزیابی قلب، کنترل فشار خون و برنامه درمانی شخصی برای سلامت قلب شما.',
      'Heart evaluation, blood-pressure control, and a personal plan for your cardiac health.',
    ),
    bannerTitle: t(
      'نوبت قلب را آنلاین رزرو کنید — ظرفیت ویزیت محدود است.',
      'Book a cardiology visit online — appointment slots are limited.',
    ),
    doctorIntro: t(
      'تمرکز این مطب بر تشخیص و پیگیری بیماری‌های قلبی، فشار خون و پیشگیری از عوارض قلبی است. ویزیت با شرح‌حال دقیق، تفسیر نوار قلب و برنامه دارویی شفاف انجام می‌شود.',
      'This practice focuses on diagnosing and following heart disease, hypertension, and prevention. Visits include a careful history, ECG review, and a clear medication plan.',
    ),
    services: [
      svc('اکوکاردیوگرافی', 'Echocardiography', 'بررسی ساختار و عملکرد قلب با تصویربرداری.', 'Assess heart structure and function with imaging.', [
        ['تفسیر همان جلسه', 'Same-visit interpretation'],
        ['گزارش مکتوب', 'Written report'],
        ['توصیه پیگیری', 'Follow-up advice'],
      ]),
      svc('نوار قلب', 'ECG', 'ثبت فعالیت الکتریکی قلب برای تشخیص آریتمی و ایسکمی.', 'Record electrical activity to detect arrhythmia and ischemia.', [
        ['انجام سریع', 'Quick test'],
        ['مقایسه با نوارهای قبلی', 'Compare with prior ECGs'],
        ['راهنمای درمان', 'Treatment guidance'],
      ]),
      svc('مشاوره فشار خون', 'Blood pressure consult', 'تنظیم دارو و سبک زندگی برای کنترل پایدار فشار.', 'Medication and lifestyle tuning for stable blood-pressure control.', [
        ['برنامه پایش خانگی', 'Home monitoring plan'],
        ['تنظیم دارو', 'Medication adjustment'],
        ['آموزش تغذیه کم‌نمک', 'Low-salt diet coaching'],
      ]),
    ],
    faq: [
      { label: t('چه زمانی باید به متخصص قلب مراجعه کنم؟', 'When should I see a cardiologist?'), content: t('درد قفسه سینه، تنگی نفس، تپش نامنظم، فشار خون کنترل‌نشده یا سابقه خانوادگی بیماری قلبی نیاز به بررسی دارد.', 'Chest pain, shortness of breath, irregular palpitations, uncontrolled blood pressure, or a family history of heart disease warrant evaluation.') },
      { label: t('نوار قلب چقدر طول می‌کشد؟', 'How long does an ECG take?'), content: t('معمولاً چند دقیقه است و همان جلسه تفسیر می‌شود.', 'Usually a few minutes, with interpretation during the same visit.') },
      { label: t('آیا اکو نیاز به آمادگی خاصی دارد؟', 'Does echocardiography need preparation?'), content: t('اغلب ناشتا بودن لازم نیست؛ لباس راحت بپوشید و داروهای قلبی را طبق دستور ادامه دهید مگر خلافش گفته شود.', 'Fasting is rarely required; wear comfortable clothing and continue cardiac medicines unless told otherwise.') },
    ],
    testimonials: [
      { name: t('مریم', 'Maryam'), comment: t('توضیح نوار قلب واضح بود و برنامه دارویی‌ام منظم شد.', 'The ECG explanation was clear and my medication plan became organized.') },
      { name: t('حسین', 'Hossein'), comment: t('پیگیری فشار خون دقیق و بدون عجله انجام شد.', 'Blood-pressure follow-up was thorough and unhurried.') },
    ],
  },
  dermatology: {
    heroTitle: t('مراقبت تخصصی پوست و زیبایی', 'Specialist skin and aesthetic care'),
    heroDescription: t(
      'درمان آکنه، ضایعات پوستی و مشاوره زیبایی با برنامه متناسب پوست شما.',
      'Acne care, skin lesions, and aesthetic consults tailored to your skin.',
    ),
    bannerTitle: t(
      'برای ویزیت پوست نوبت آنلاین بگیرید.',
      'Book a dermatology visit online.',
    ),
    doctorIntro: t(
      'این مطب روی تشخیص بیماری‌های پوست، مو و ناخن و درمان‌های محافظه‌کارانه زیبایی تمرکز دارد. هدف، نتیجه پایدار با کمترین عارضه است.',
      'This practice focuses on skin, hair, and nail disease plus conservative aesthetic care. The goal is lasting results with minimal side effects.',
    ),
    services: [
      svc('درمان آکنه', 'Acne treatment', 'برنامه دارویی و مراقبت خانگی برای کنترل جوش.', 'A medication and home-care plan to control acne.', [
        ['ارزیابی نوع پوست', 'Skin-type assessment'],
        ['رژیم درمانی مرحله‌ای', 'Staged treatment plan'],
        ['پیگیری ماهانه', 'Monthly follow-up'],
      ]),
      svc('لیزر پوست', 'Skin laser', 'مشاوره و برنامه جلسات لیزر متناسب با هدف درمانی.', 'Consultation and a laser session plan matched to your goal.', [
        ['انتخاب پروتکل مناسب', 'Protocol selection'],
        ['توضیح مراقبت بعد از جلسه', 'Aftercare briefing'],
        ['زمان‌بندی جلسات', 'Session scheduling'],
      ]),
      svc('مشاوره زیبایی', 'Aesthetic consult', 'بررسی نیاز پوست و پیشنهاد درمان غیرتهاجمی.', 'Review skin needs and suggest non-invasive options.', [
        ['برنامه مراقبت روزانه', 'Daily skincare plan'],
        ['اولویت‌بندی درمان', 'Treatment priorities'],
        ['انتظار واقع‌بینانه از نتیجه', 'Realistic outcome talk'],
      ]),
    ],
    faq: [
      { label: t('درمان آکنه چقدر طول می‌کشد؟', 'How long does acne treatment take?'), content: t('معمولاً چند هفته تا چند ماه؛ پیگیری منظم نتیجه را پایدارتر می‌کند.', 'Usually weeks to months; regular follow-up makes results more stable.') },
      { label: t('آیا لیزر برای همه مناسب است؟', 'Is laser suitable for everyone?'), content: t('بستگی به نوع پوست، داروهای مصرفی و هدف درمان دارد و در ویزیت مشخص می‌شود.', 'It depends on skin type, medications, and the treatment goal, decided at the visit.') },
      { label: t('قبل از ویزیت پوست چه کار کنم؟', 'What should I do before a skin visit?'), content: t('لیست محصولات و داروهای پوستی را بیاورید و آرایش سنگین نکنید.', 'Bring a list of skincare products and medicines, and avoid heavy makeup.') },
    ],
    testimonials: [
      { name: t('نگار', 'Negar'), comment: t('برنامه آکنه ساده بود و پوستم آرام‌تر شد.', 'The acne plan was simple and my skin calmed down.') },
      { name: t('رضا', 'Reza'), comment: t('مشاوره زیبایی بدون اغراق و شفاف بود.', 'The aesthetic consult was honest and clear.') },
    ],
  },
  dentistry: {
    heroTitle: t('مراقبت تخصصی دندانپزشکی', 'Specialist dental care'),
    heroDescription: t(
      'جرم‌گیری، درمان ریشه و ایمپلنت با برنامه درمانی شفاف.',
      'Scaling, root canal, and implants with a clear treatment plan.',
    ),
    bannerTitle: t(
      'نوبت دندانپزشکی را آنلاین رزرو کنید.',
      'Book a dental appointment online.',
    ),
    doctorIntro: t(
      'این مطب روی سلامت دهان و دندان، درمان درد و بازسازی طرح لبخند با توضیح مرحله‌به‌مرحله هزینه‌ها و جلسات تمرکز دارد.',
      'This practice focuses on oral health, pain treatment, and smile restoration, with a step-by-step explanation of visits and costs.',
    ),
    services: [
      svc('جرم‌گیری', 'Scaling', 'برداشت جرم و آموزش بهداشت برای پیشگیری از التهاب لثه.', 'Remove tartar and teach hygiene to prevent gum inflammation.', [
        ['پاکسازی حرفه‌ای', 'Professional cleaning'],
        ['بررسی لثه', 'Gum check'],
        ['برنامه مسواک و نخ', 'Brushing and floss plan'],
      ]),
      svc('عصب‌کشی', 'Root canal', 'درمان ریشه برای حفظ دندان دردناک یا عفونی.', 'Root treatment to save a painful or infected tooth.', [
        ['کنترل درد', 'Pain control'],
        ['جلسات مشخص', 'Defined visits'],
        ['ترمیم پس از درمان', 'Post-treatment restoration'],
      ]),
      svc('ایمپلنت', 'Implants', 'جایگزینی دندان از دست‌رفته با برنامه تصویربرداری و جراحی.', 'Replace a missing tooth with imaging and a surgical plan.', [
        ['بررسی استخوان', 'Bone assessment'],
        ['برنامه چندمرحله‌ای', 'Staged plan'],
        ['پیگیری جوش‌خوردن', 'Osseointegration follow-up'],
      ]),
    ],
    faq: [
      { label: t('جرم‌گیری درد دارد؟', 'Does scaling hurt?'), content: t('معمولاً ناراحتی خفیف است؛ در حساسیت بالا بی‌حسی موضعی استفاده می‌شود.', 'Usually mild discomfort; local anesthetic is used if you are sensitive.') },
      { label: t('عصب‌کشی در چند جلسه تمام می‌شود؟', 'How many visits for a root canal?'), content: t('بسته به دندان، یک تا چند جلسه؛ در ویزیت اول زمان‌بندی گفته می‌شود.', 'One to several visits depending on the tooth; the first visit sets the schedule.') },
      { label: t('ایمپلنت برای همه مناسب است؟', 'Are implants for everyone?'), content: t('سلامت عمومی، استخوان فک و بهداشت دهان تعیین‌کننده است و با تصویربرداری مشخص می‌شود.', 'General health, jaw bone, and oral hygiene decide this, confirmed with imaging.') },
    ],
    testimonials: [
      { name: t('سارا', 'Sara'), comment: t('عصب‌کشی بدون درد غیرمنتظره و با توضیح کامل انجام شد.', 'The root canal was explained fully and without unexpected pain.') },
      { name: t('امیر', 'Amir'), comment: t('بعد از جرم‌گیری برنامه بهداشت دهانم عوض شد.', 'After scaling, my oral-hygiene routine actually changed.') },
    ],
  },
  internal: {
    heroTitle: t('مراقبت تخصصی داخلی', 'Internal medicine care'),
    heroDescription: t(
      'ویزیت عمومی، چکاپ دوره‌ای و پیگیری بیماری‌های مزمن با رویکرد جامع.',
      'General visits, periodic checkups, and chronic-disease follow-up with a whole-person approach.',
    ),
    bannerTitle: t(
      'برای ویزیت داخلی نوبت آنلاین بگیرید.',
      'Book an internal-medicine visit online.',
    ),
    doctorIntro: t(
      'این مطب روی تشخیص افتراقی، مدیریت بیماری‌های مزمن و پیشگیری با آزمایش و پیگیری منظم تمرکز دارد تا درمان پراکنده نشود.',
      'This practice focuses on differential diagnosis, chronic-disease management, and prevention with labs and regular follow-up so care stays coordinated.',
    ),
    services: [
      svc('ویزیت عمومی', 'General visit', 'بررسی علائم، شرح‌حال و برنامه تشخیصی اولیه.', 'Review symptoms, history, and an initial diagnostic plan.', [
        ['معاینه کامل', 'Full examination'],
        ['درخواست آزمایش هدفمند', 'Targeted lab orders'],
        ['برنامه پیگیری', 'Follow-up plan'],
      ]),
      svc('چکاپ دوره‌ای', 'Periodic checkup', 'غربالگری متناسب با سن و عوامل خطر.', 'Age- and risk-based screening.', [
        ['مرور داروهای جاری', 'Medication review'],
        ['غربالگری استاندارد', 'Standard screening'],
        ['توصیه سبک زندگی', 'Lifestyle advice'],
      ]),
      svc('مشاوره تغذیه', 'Nutrition advice', 'راهنمای تغذیه در کنار درمان داخلی، نه رژیم تبلیغاتی.', 'Nutrition guidance alongside medical care — not a fad diet.', [
        ['ارزیابی عادات غذایی', 'Diet-habit review'],
        ['اهداف واقع‌بینانه', 'Realistic goals'],
        ['هماهنگی با بیماری زمینه‌ای', 'Aligned with existing conditions'],
      ]),
    ],
    faq: [
      { label: t('چکاپ از چه سنی لازم است؟', 'From what age do I need checkups?'), content: t('بسته به سن، سابقه خانوادگی و بیماری‌های زمینه‌ای؛ در ویزیت برنامه شخصی ساخته می‌شود.', 'It depends on age, family history, and existing conditions; a personal plan is set at the visit.') },
      { label: t('آزمایش‌ها را کجا انجام دهم؟', 'Where do I do the labs?'), content: t('آزمایش‌های لازم در نسخه مشخص می‌شود؛ می‌توانید آزمایشگاه نزدیک خود را انتخاب کنید.', 'Required tests are listed on the order; you can choose a nearby lab.') },
      { label: t('اگر چند بیماری با هم داشته باشم چه می‌شود؟', 'What if I have several conditions?'), content: t('داروها و تداخل‌ها یکجا مرور می‌شود تا برنامه واحد داشته باشید.', 'Medicines and interactions are reviewed together so you have one coordinated plan.') },
    ],
    testimonials: [
      { name: t('لیلا', 'Leila'), comment: t('چکاپ منظم شد و داروهایم ساده‌تر شد.', 'Checkups became regular and my medicines were simplified.') },
      { name: t('کامران', 'Kamran'), comment: t('توضیح آزمایش‌ها قابل فهم بود.', 'The lab explanations were easy to understand.') },
    ],
  },
  pediatrics: {
    heroTitle: t('مراقبت تخصصی کودکان', 'Specialist pediatric care'),
    heroDescription: t(
      'واکسیناسیون، پایش رشد و درمان بیماری‌های شایع کودکان با همراهی خانواده.',
      'Vaccination, growth monitoring, and care for common childhood illnesses — with the family involved.',
    ),
    bannerTitle: t(
      'نوبت اطفال را آنلاین رزرو کنید.',
      'Book a pediatrics visit online.',
    ),
    doctorIntro: t(
      'این مطب روی رشد سالم، واکسن به‌موقع و درمان آرام بیماری‌های شایع تمرکز دارد. والدین در هر مرحله توضیح واضح می‌گیرند.',
      'This practice focuses on healthy growth, on-time vaccines, and calm treatment of common illnesses. Parents get a clear explanation at every step.',
    ),
    services: [
      svc('واکسیناسیون', 'Vaccination', 'برنامه واکسن مطابق سن و سابقه کودک.', 'A vaccine schedule matched to the child’s age and history.', [
        ['مرور کارت واکسن', 'Vaccine-card review'],
        ['توضیح عوارض شایع', 'Common side-effect briefing'],
        ['یادآوری نوبت بعدی', 'Next-dose reminder'],
      ]),
      svc('رشد و تکامل', 'Growth monitoring', 'پایش قد، وزن و نقاط عطف تکاملی.', 'Track height, weight, and developmental milestones.', [
        ['منحنی رشد', 'Growth chart'],
        ['غربالگری تکامل', 'Development screen'],
        ['راهنمای تغذیه سنی', 'Age-based feeding advice'],
      ]),
      svc('بیماری‌های شایع کودکان', 'Common childhood illnesses', 'تب، عفونت تنفسی و مشکلات گوارشی با معیار ارجاع واضح.', 'Fever, respiratory infection, and tummy issues with clear referral criteria.', [
        ['معیار خطر', 'Red-flag criteria'],
        ['دوز داروی اطفال', 'Pediatric dosing'],
        ['آموزش مراقبت خانگی', 'Home-care teaching'],
      ]),
    ],
    faq: [
      { label: t('اگر کودک تب داشت چه کنم؟', 'What if my child has a fever?'), content: t('مایعات، تب‌بر مناسب سن، و مراجعه فوری در بی‌حالی، تشنج یا تنفس سخت.', 'Fluids, age-appropriate fever medicine, and urgent care for lethargy, seizure, or hard breathing.') },
      { label: t('واکسن را می‌توان عقب انداخت؟', 'Can vaccines be delayed?'), content: t('تأخیر غیرضروری توصیه نمی‌شود؛ در بیماری حاد، زمان‌بندی در ویزیت تنظیم می‌شود.', 'Unnecessary delay is not advised; during acute illness the schedule is adjusted at the visit.') },
      { label: t('چند وقت یک‌بار پایش رشد لازم است؟', 'How often is growth monitoring needed?'), content: t('در شیرخوارگی پرتکرارتر است و بعد با فاصله بیشتر؛ برنامه روی کارت رشد ثبت می‌شود.', 'More often in infancy, then less frequently; the plan is recorded on the growth card.') },
    ],
    testimonials: [
      { name: t('فاطمه', 'Fatemeh'), comment: t('با حوصله به نگرانی‌ام درباره تب گوش داد.', 'They patiently listened to my worry about the fever.') },
      { name: t('مهدی', 'Mehdi'), comment: t('برنامه واکسن شفاف شد و یادآوری گرفتیم.', 'The vaccine schedule became clear and we got reminders.') },
    ],
  },
  orthopedics: {
    heroTitle: t('مراقبت تخصصی ارتوپدی', 'Specialist orthopedic care'),
    heroDescription: t(
      'آسیب ورزشی، درد مفاصل و مسیر توانبخشی با برنامه مرحله‌ای.',
      'Sports injuries, joint pain, and a staged rehab path.',
    ),
    bannerTitle: t(
      'برای ویزیت ارتوپدی نوبت آنلاین بگیرید.',
      'Book an orthopedic visit online.',
    ),
    doctorIntro: t(
      'این مطب روی درد اسکلتی‌عضلانی، آسیب ورزشی و تصمیم‌گیری بین درمان محافظه‌کارانه و ارجاع جراحی تمرکز دارد.',
      'This practice focuses on musculoskeletal pain, sports injury, and deciding between conservative care and surgical referral.',
    ),
    services: [
      svc('درمان آسیب ورزشی', 'Sports injury care', 'ارزیابی پیچ‌خوردگی، کشیدگی و بازگشت ایمن به ورزش.', 'Assess sprains and strains and plan a safe return to sport.', [
        ['معاینه عملکردی', 'Functional exam'],
        ['برنامه استراحت فعال', 'Active rest plan'],
        ['معیار بازگشت به تمرین', 'Return-to-play criteria'],
      ]),
      svc('درد مفاصل', 'Joint pain', 'بررسی زانو، شانه و کمر با تصویربرداری در صورت نیاز.', 'Evaluate knee, shoulder, and back pain with imaging when needed.', [
        ['تشخیص افتراقی', 'Differential diagnosis'],
        ['درمان دارویی هدفمند', 'Targeted medication'],
        ['تمرین خانگی', 'Home exercises'],
      ]),
      svc('فیزیوتراپی', 'Physiotherapy', 'هماهنگی توانبخشی برای بازیابی حرکت و قدرت.', 'Coordinate rehab to restore motion and strength.', [
        ['اهداف حرکتی', 'Mobility goals'],
        ['جلسات منظم', 'Regular sessions'],
        ['پیشگیری از عود', 'Relapse prevention'],
      ]),
    ],
    faq: [
      { label: t('کی باید عکس یا ام‌آرآی بگیرم؟', 'When do I need an X-ray or MRI?'), content: t('بعد از معاینه؛ همه دردها نیاز به تصویربرداری فوری ندارند.', 'After the exam; not every pain needs immediate imaging.') },
      { label: t('آسیب ورزشی را کی می‌توانم دوباره تمرین کنم؟', 'When can I train again after a sports injury?'), content: t('وقتی درد و تورم کنترل شود و معیارهای عملکردی در ویزیت تأیید شود.', 'When pain and swelling are controlled and functional criteria are met at the visit.') },
      { label: t('آیا همیشه جراحی لازم است؟', 'Is surgery always needed?'), content: t('خیر؛ بسیاری از موارد با فیزیوتراپی و تغییر فعالیت بهتر می‌شوند.', 'No; many cases improve with physiotherapy and activity modification.') },
    ],
    testimonials: [
      { name: t('نیما', 'Nima'), comment: t('بعد از پیچ‌خوردگی برنامه برگشت به فوتبال مشخص شد.', 'After the sprain, the return-to-football plan was clear.') },
      { name: t('زهرا', 'Zahra'), comment: t('درد زانو با تمرین‌ها کمتر شد و عجله برای جراحی نبود.', 'Knee pain eased with exercises and there was no rush to surgery.') },
    ],
  },
  gynecology: {
    heroTitle: t('مراقبت تخصصی زنان', 'Specialist gynecology care'),
    heroDescription: t(
      'ویزیت زنان، مراقبت بارداری و سونوگرافی با فضای محرمانه و توضیح شفاف.',
      'Gynecology visits, prenatal care, and ultrasound in a private setting with clear explanations.',
    ),
    bannerTitle: t(
      'نوبت زنان را آنلاین رزرو کنید.',
      'Book a gynecology visit online.',
    ),
    doctorIntro: t(
      'این مطب روی سلامت باروری، مراقبت بارداری و پیگیری مشکلات شایع زنان با احترام به حریم خصوصی تمرکز دارد.',
      'This practice focuses on reproductive health, prenatal care, and common gynecologic issues, with respect for privacy.',
    ),
    services: [
      svc('ویزیت زنان', 'Gynecology visit', 'بررسی درد، اختلال قاعدگی و غربالگری دوره‌ای.', 'Evaluate pain, menstrual issues, and routine screening.', [
        ['شرح‌حال محرمانه', 'Confidential history'],
        ['برنامه تشخیصی', 'Diagnostic plan'],
        ['آموزش مراقبت', 'Self-care teaching'],
      ]),
      svc('مراقبت بارداری', 'Prenatal care', 'پیگیری مادر و جنین در طول بارداری.', 'Follow mother and baby throughout pregnancy.', [
        ['جدول ویزیت‌ها', 'Visit schedule'],
        ['غربالگری استاندارد', 'Standard screening'],
        ['آموزش علائم خطر', 'Warning-sign education'],
      ]),
      svc('سونوگرافی', 'Ultrasound', 'تصویربرداری تشخیصی با توضیح نتیجه همان جلسه.', 'Diagnostic imaging with same-visit explanation.', [
        ['تفسیر واضح', 'Clear interpretation'],
        ['گزارش مکتوب', 'Written report'],
        ['برنامه گام بعد', 'Next-step plan'],
      ]),
    ],
    faq: [
      { label: t('فاصله ویزیت‌های بارداری چقدر است؟', 'How often are prenatal visits?'), content: t('در سه‌ماهه اول معمولاً ماهانه و بعد پرتکرارتر؛ برنامه شخصی در سابقه ثبت می‌شود.', 'Often monthly in the first trimester, then more frequent; your schedule is recorded in the chart.') },
      { label: t('سونوگرافی نیاز به آمادگی دارد؟', 'Does ultrasound need preparation?'), content: t('بسته به نوع سونو ممکن است مثانه پر لازم باشد؛ هنگام رزرو گفته می‌شود.', 'Some scans need a full bladder; you will be told when booking.') },
      { label: t('آیا ویزیت محرمانه است؟', 'Is the visit confidential?'), content: t('بله؛ اطلاعات فقط با رضایت شما و در چارچوب مراقبت پزشکی استفاده می‌شود.', 'Yes; information is used only with your consent within medical care.') },
    ],
    testimonials: [
      { name: t('هستی', 'Hasti'), comment: t('مراقبت بارداری منظم و بدون اضطراب اضافه بود.', 'Prenatal care was regular without extra anxiety.') },
      { name: t('مینا', 'Mina'), comment: t('نتیجه سونو همان جلسه با حوصله توضیح داده شد.', 'The ultrasound was explained patiently in the same visit.') },
    ],
  },
  psychiatry: {
    heroTitle: t('مراقبت تخصصی روانپزشکی', 'Specialist psychiatric care'),
    heroDescription: t(
      'مشاوره اضطراب، درمان افسردگی و روان‌درمانی در فضایی امن و بدون قضاوت.',
      'Anxiety counseling, depression treatment, and psychotherapy in a safe, non-judgmental space.',
    ),
    bannerTitle: t(
      'برای جلسه روانپزشکی نوبت آنلاین بگیرید.',
      'Book a psychiatry session online.',
    ),
    doctorIntro: t(
      'این مطب روی اضطراب، خلق و خواب با ترکیب دارو (در صورت نیاز) و مهارت‌های روان‌درمانی تمرکز دارد. روند درمان شفاف و مرحله‌ای است.',
      'This practice focuses on anxiety, mood, and sleep with medication when needed plus psychotherapy skills. Treatment is staged and transparent.',
    ),
    services: [
      svc('مشاوره اضطراب', 'Anxiety counseling', 'شناخت محرک‌ها و مهارت آرام‌سازی.', 'Identify triggers and learn calming skills.', [
        ['ارزیابی شدت علائم', 'Symptom-severity review'],
        ['تمرین مهارت', 'Skills practice'],
        ['برنامه بین جلسات', 'Between-session plan'],
      ]),
      svc('درمان افسردگی', 'Depression treatment', 'برنامه دارویی و پیگیری خلق با ویزیت منظم.', 'A medication plan and regular mood follow-up.', [
        ['غربالگری ایمنی', 'Safety screening'],
        ['تنظیم دارو', 'Medication titration'],
        ['پیگیری عوارض', 'Side-effect follow-up'],
      ]),
      svc('جلسه روان‌درمانی', 'Psychotherapy session', 'گفت‌وگوی ساختاریافته برای تغییر الگوی فکر و رفتار.', 'Structured conversation to shift thought and behavior patterns.', [
        ['اهداف جلسه', 'Session goals'],
        ['تکلیف خانگی کوتاه', 'Short homework'],
        ['مرور پیشرفت', 'Progress review'],
      ]),
    ],
    faq: [
      { label: t('آیا حتماً دارو لازم است؟', 'Is medication always required?'), content: t('خیر؛ بسته به شدت علائم ممکن است روان‌درمانی کافی باشد یا ترکیب توصیه شود.', 'No; depending on severity, therapy alone or a combination may be recommended.') },
      { label: t('جلسات محرمانه است؟', 'Are sessions confidential?'), content: t('بله، مگر در خطر جدی برای خود یا دیگران که قانون افشا را ایجاب کند.', 'Yes, except when serious risk to self or others legally requires disclosure.') },
      { label: t('چند جلسه لازم است؟', 'How many sessions will I need?'), content: t('پس از ارزیابی اولیه تخمین زده می‌شود و با پیشرفت بازنگری می‌شود.', 'An estimate is given after the first assessment and revised with progress.') },
    ],
    testimonials: [
      { name: t('آیدا', 'Aida'), comment: t('فضای جلسه امن بود و برای اضطراب ابزار عملی گرفتم.', 'The session felt safe and I got practical tools for anxiety.') },
      { name: t('سینا', 'Sina'), comment: t('تنظیم دارو با پیگیری منظم و بدون عجله انجام شد.', 'Medication was adjusted with regular, unhurried follow-up.') },
    ],
  },
  nutrition: {
    heroTitle: t('مشاوره تخصصی تغذیه', 'Specialist nutrition care'),
    heroDescription: t(
      'رژیم کاهش وزن، تغذیه ورزشی و مشاوره دیابت با برنامه قابل اجرا.',
      'Weight-loss plans, sports nutrition, and diabetes advice you can actually follow.',
    ),
    bannerTitle: t(
      'نوبت مشاوره تغذیه را آنلاین رزرو کنید.',
      'Book a nutrition consult online.',
    ),
    doctorIntro: t(
      'این مطب روی تغییر پایدار عادات غذایی تمرکز دارد، نه رژیم کوتاه‌مدت. برنامه با آزمایش، بیماری زمینه‌ای و سبک زندگی شما هماهنگ می‌شود.',
      'This practice focuses on lasting habit change, not short diets. Plans are aligned with labs, medical conditions, and your lifestyle.',
    ),
    services: [
      svc('رژیم کاهش وزن', 'Weight-loss plan', 'کسری کالری منطقی با حفظ عضله و پیگیری وزن.', 'A sensible calorie deficit that preserves muscle, with weight follow-up.', [
        ['هدف وزنی واقع‌بینانه', 'Realistic weight goal'],
        ['منوی نمونه هفتگی', 'Sample weekly menu'],
        ['پایش دور کمر و وزن', 'Waist and weight tracking'],
      ]),
      svc('تغذیه ورزشی', 'Sports nutrition', 'زمان‌بندی وعده و مایعات برای تمرین و مسابقه.', 'Meal and fluid timing for training and competition.', [
        ['نیاز پروتئین و کربوهیدرات', 'Protein and carb targets'],
        ['برنامه روز تمرین', 'Training-day plan'],
        ['بازیابی بعد از جلسه', 'Post-session recovery'],
      ]),
      svc('مشاوره دیابت', 'Diabetes nutrition', 'توزیع کربوهیدرات و هماهنگی با دارو یا انسولین.', 'Carb distribution coordinated with medication or insulin.', [
        ['آموزش برچسب مواد غذایی', 'Food-label teaching'],
        ['الگوی بشقاب', 'Plate method'],
        ['پیگیری قند', 'Glucose follow-up'],
      ]),
    ],
    faq: [
      { label: t('رژیم چقدر طول می‌کشد؟', 'How long does a diet last?'), content: t('کاهش وزن پایدار ماه‌ها طول می‌کشد؛ هدف عادت است نه عدد یک‌هفته‌ای.', 'Sustainable loss takes months; the goal is habits, not a one-week number.') },
      { label: t('مکمل لازم است؟', 'Do I need supplements?'), content: t('فقط در کمبود اثبات‌شده یا نیاز ورزشی مشخص؛ اول غذا، بعد مکمل.', 'Only for documented deficiency or a clear sports need; food first, then supplements.') },
      { label: t('اگر دیابت داشته باشم رژیم جدا می‌شود؟', 'If I have diabetes, is the plan different?'), content: t('بله؛ کربوهیدرات، زمان وعده و دارو با هم تنظیم می‌شود.', 'Yes; carbs, meal timing, and medication are coordinated.') },
    ],
    testimonials: [
      { name: t('شیدا', 'Sheida'), comment: t('رژیم قابل اجرا بود و گرسنگی مداوم نداشتم.', 'The plan was doable and I was not constantly hungry.') },
      { name: t('بهرام', 'Bahram'), comment: t('برای تمرین، زمان‌بندی وعده‌ها خیلی کمک کرد.', 'Meal timing for training helped a lot.') },
    ],
  },
  other: {
    heroTitle: t('مراقبت تخصصی پزشکی', 'Specialist medical care'),
    heroDescription: t(
      'مشاوره تخصصی، ویزیت حضوری و پیگیری درمان با برنامه شفاف.',
      'Specialist consults, in-person visits, and treatment follow-up with a clear plan.',
    ),
    bannerTitle: t(
      'نوبت خود را آنلاین رزرو کنید.',
      'Book your appointment online.',
    ),
    doctorIntro: t(
      'این مطب روی ویزیت دقیق، توضیح تشخیص و پیگیری منظم تمرکز دارد تا مسیر درمان برای بیمار روشن بماند.',
      'This practice focuses on careful visits, a clear diagnosis explanation, and regular follow-up so the treatment path stays understandable.',
    ),
    services: [
      svc('مشاوره تخصصی', 'Specialist consult', 'بررسی سوابق و نظر دوم در صورت نیاز.', 'Chart review and a second opinion when needed.', [
        ['مرور سوابق', 'Record review'],
        ['برنامه تشخیصی', 'Diagnostic plan'],
        ['پاسخ به پرسش‌ها', 'Time for questions'],
      ]),
      svc('ویزیت حضوری', 'In-person visit', 'معاینه کامل و تصمیم‌گیری درمانی همان جلسه.', 'Full exam and a same-visit treatment decision.', [
        ['معاینه بالینی', 'Clinical exam'],
        ['نسخه و آموزش', 'Prescription and teaching'],
        ['زمان پیگیری', 'Follow-up timing'],
      ]),
      svc('پیگیری درمان', 'Follow-up care', 'بازبینی پاسخ به درمان و تنظیم برنامه.', 'Review treatment response and adjust the plan.', [
        ['مرور علائم', 'Symptom review'],
        ['تنظیم دارو', 'Medication adjustment'],
        ['معیار بهبود', 'Improvement criteria'],
      ]),
    ],
    faq: [
      { label: t('اولین ویزیت چه مدارکی ببرم؟', 'What should I bring to the first visit?'), content: t('لیست دارو، آزمایش‌های قبلی و خلاصه سوابق پزشکی.', 'A medication list, prior labs, and a brief medical history.') },
      { label: t('نوبت آنلاین قطعی است؟', 'Is an online booking confirmed?'), content: t('پس از ثبت، زمان در پیام تأیید می‌شود؛ در صورت تغییر با شما هماهنگ می‌شود.', 'After booking, the time is confirmed by message; changes are coordinated with you.') },
      { label: t('پیگیری حضوری لازم است یا تلفنی؟', 'Is follow-up in person or by phone?'), content: t('بسته به نوع درمان در ویزیت اول مشخص می‌شود.', 'It is decided at the first visit based on the treatment type.') },
    ],
    testimonials: [
      { name: t('الهام', 'Elham'), comment: t('مسیر درمان از همان جلسه اول مشخص شد.', 'The treatment path was clear from the first visit.') },
      { name: t('پوریا', 'Pouria'), comment: t('پیگیری منظم بود و سؤالاتم بی‌پاسخ نماند.', 'Follow-up was regular and my questions were answered.') },
    ],
  },
}

export function getSpecialtyContent(key: SpecialtyKey): SpecialtyContent {
  return SPECIALTY_CONTENT[key] ?? SPECIALTY_CONTENT.other
}
